/**
 * SearchInput Component
 * 
 * Unified search input with:
 * - Browser autofill blocking
 * - Optional search icon
 * - Optional debounce
 * - Design system tokens
 * 
 * SOURCE OF TRUTH for all search inputs across the application.
 */

import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { FORM_CONTROLS } from "@/design-system";
import { useLocalizations } from "@/hooks/useLocalizations";

export interface SearchInputProps extends Omit<React.ComponentProps<"input">, 'type' | 'size'> {
  /** Show search icon (default: true) */
  showIcon?: boolean;
  /** Debounce delay in milliseconds (0 = no debounce) */
  debounceMs?: number;
  /** Callback for debounced value changes */
  onDebouncedChange?: (value: string) => void;
  /** Container className overrides */
  containerClassName?: string;
  /** Size variant */
  size?: 'default' | 'compact';
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({
    showIcon = true,
    debounceMs = 0,
    onDebouncedChange,
    containerClassName,
    className,
    placeholder,
    disabled = false,
    size = 'default',
    name,
    value,
    onChange,
    ...props
  }, ref) => {
    const { lCommon } = useLocalizations();
    const resolvedPlaceholder = placeholder ?? lCommon('search_input.placeholder', { defaultValue: 'Search...' });
    const [localValue, setLocalValue] = useState(value as string || '');
    
    // Generate random name to prevent browser autofill matching
    const randomName = React.useMemo(
      () => name || `search-${Math.random().toString(36).slice(2)}`,
      [name]
    );

    // Sync local value with external value
    useEffect(() => {
      if (value !== undefined) {
        setLocalValue(value as string);
      }
    }, [value]);

    // Debounced onChange handler
    useEffect(() => {
      if (debounceMs === 0 || !onDebouncedChange) return;
      
      const timer = setTimeout(() => {
        onDebouncedChange(localValue);
      }, debounceMs);

      return () => clearTimeout(timer);
    }, [localValue, debounceMs, onDebouncedChange]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      onChange?.(e);
    }, [onChange]);

    const heightClass = size === 'compact' ? FORM_CONTROLS.height.compact : FORM_CONTROLS.height.default;

    return (
      <div className={cn(FORM_CONTROLS.searchWrapper.container, containerClassName)}>
        {showIcon && (
          <Search className={FORM_CONTROLS.searchWrapper.icon} />
        )}
        <input
          ref={ref}
          type="search"
          name={randomName}
          placeholder={resolvedPlaceholder}
          value={value !== undefined ? value : localValue}
          onChange={handleChange}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-protonpass-ignore
          aria-autocomplete="none"
          className={cn(
            "flex w-full rounded-md border border-input bg-background px-3 py-2 text-base input-interactive file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            heightClass,
            showIcon && "pl-9",
            className,
          )}
          {...props}
        />
      </div>
    );
  }
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
